import { addDays, addWeeks, format, getWeek, isSameDay, startOfWeek, subWeeks } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/libs/utils'

interface WeekNavigatorProps {
  currentWeekStart: Date
  onWeekChange: (weekStart: Date) => void
  className?: string
}

export function WeekNavigator({ currentWeekStart, onWeekChange, className }: WeekNavigatorProps) {
  const { t } = useTranslation()

  const weekEnd = addDays(currentWeekStart, 6)
  const weekNumber = getWeek(currentWeekStart, { weekStartsOn: 1 })
  const dateLabel = `${format(currentWeekStart, 'MMM d')} – ${format(weekEnd, 'MMM d, yyyy')}`

  const isCurrentWeek = useMemo(() => {
    const nowWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
    return isSameDay(currentWeekStart, nowWeekStart)
  }, [currentWeekStart])

  const handlePrev = () => onWeekChange(subWeeks(currentWeekStart, 1))
  const handleNext = () => onWeekChange(addWeeks(currentWeekStart, 1))
  const goToToday = () => onWeekChange(startOfWeek(new Date(), { weekStartsOn: 1 }))

  const handleCalendarSelect = (date: Date | undefined) => {
    if (date) {
      onWeekChange(startOfWeek(date, { weekStartsOn: 1 }))
    }
  }

  return (
    <div className={cn('relative flex items-center justify-center gap-2', className)}>
      {!isCurrentWeek && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 rounded-full absolute right-full mr-2"
          onClick={goToToday}
        >
          {t('scheduler.today')}
        </Button>
      )}
      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8"
        onClick={handlePrev}
        aria-label={t('common.previous')}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <Popover>
        <PopoverTrigger asChild>
          <button className="text-center min-w-[180px] px-3 py-1.5 rounded-md hover:bg-muted/60 transition-colors cursor-pointer">
            <p className="text-sm font-bold tracking-tight">{dateLabel}</p>
            <p className="text-[11px] text-muted-foreground/80 mt-0.5">
              {t('scheduler.week')} {weekNumber}
            </p>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="center">
          <Calendar
            mode="single"
            selected={currentWeekStart}
            onSelect={handleCalendarSelect}
            className={cn('p-3 pointer-events-auto')}
            modifiers={{ weekRange: { from: currentWeekStart, to: weekEnd } }}
            modifiersStyles={{
              weekRange: {
                backgroundColor: 'hsl(var(--primary) / 0.1)',
                borderRadius: 0,
              },
            }}
          />
        </PopoverContent>
      </Popover>
      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8"
        onClick={handleNext}
        aria-label={t('common.next')}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  )
}
